import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { useForm, FormProvider } from 'react-hook-form';
import { useTheme } from '@mui/material';
import { Moment } from 'moment/moment';
import moment from 'moment-timezone';
import Button from '@mui/material/Button';
import { useEffect, useRef, useState } from 'react';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseBreadcrumbs } from '@mgm/common/components/base';
import { PAGES_ANALYTICS_TITLES } from '../../utils/enums';
import { PageHelmet } from '../../components';
import { ROUTES } from '../../data';
import { InputDatePicker } from '../../components/pickers';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import {
  getAttributedOrders,
  getDorChosenDate,
  getIsOrdersLoading,
} from '../../store/orders/selectors';
import { useOrders } from '../../features/orders/Shared/hooks';
import { useOrdersByDates } from '../../features/orders/Shared/hooks/useOrdersByDates';
import { useCommonOrders } from '../../hooks/useCommonOrders';
import { ordersActions } from '../../store/orders/slice';
import { settingsActions } from '../../store/settings/slice';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { DorAnalytics } from '../../features/orders/Dor';
import { useGetDorIntegrationAPI, useGetLocationMetricsAPI } from '../../hooks/api/useIntegrations';
import { OrdersCountLoader } from '../../features/orders/Shared/components';

const DorAnalyticsPage = () => {
  const { t } = useTranslation('nav');
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const { shop } = useAppSelector(getAuthenticateUser);
  const orders = useAppSelector(getAttributedOrders);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);
  const dorChosenDate = useAppSelector(getDorChosenDate);
  const [isDorButtonDisabled, setIsDorButtonDisabled] = useState(true);
  const prevDorChosenDateRef = useRef<string | null>(null);

  const { data } = useGetDorIntegrationAPI() as any;

  useGetLocationMetricsAPI({
    isMainMetricsPage: true,
  });

  const {
    setIsBtnDisabled,
  } = useOrders();

  const { setIsFetchByDate } = useCommonOrders({
    prevTab: '',
  });

  useEffect(() => {
    if (prevDorChosenDateRef.current && !moment(prevDorChosenDateRef.current).isSame(dorChosenDate)) {
      setIsDorButtonDisabled(false);
    } else {
      setIsDorButtonDisabled(true);
    }

    prevDorChosenDateRef.current = dorChosenDate;
  }, [dorChosenDate]);

  const { getOrdersByDates } = useOrdersByDates(setIsBtnDisabled, setIsFetchByDate);

  const methods = useForm({
    mode: 'all',
    values: {
      date: moment(dorChosenDate),
    },
  });

  const onDateSubmit = async () => {
    await getOrdersByDates();
  };

  const openLocationMapping = () => {
    dispatch(settingsActions.setIsOpenDorDialog(true));
  };

  const handleDateChange = (date: Moment | null) => {
    dispatch(ordersActions.setAttributedOrders([]));
    const formattedDate = moment.tz(date, shop?.shopTimezone).startOf('day').format();

    const hasOrdersForDate = orders.some((order) => {
      const orderDate = moment.tz(order.processedAt, shop?.shopTimezone).startOf('day').format();
      return orderDate === formattedDate;
    });

    if (!hasOrdersForDate) {
      dispatch(ordersActions.setOrdersChosenDates([
        moment.tz(date, shop?.shopTimezone).startOf('week').startOf('day'),
        moment.tz(date, shop?.shopTimezone).endOf('week').endOf('day'),
      ]));
    }

    dispatch(ordersActions.setDorChosenDate(formattedDate));
  };

  return (
    <PageHelmet title={PAGES_ANALYTICS_TITLES.ANALYTICS.DOR}>
      <Container
        sx={{
          px: {
            xs: 0, lg: 2, md: 2, sm: 2,
          },
        }}
        maxWidth={false}
      >
        <BaseBreadcrumbs
          heading={t('footTraffic')}
          links={[
            { name: t('analytics'), href: ROUTES.ANALYTICS.ROOT },
            { name: t('footTraffic'), href: ROUTES.ANALYTICS.DOR },
          ]}
          action={(
            <Stack>
              <Stack pb={isMobile ? 2 : 0} spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
                <FormProvider {...methods}>
                  <InputDatePicker
                    onChange={handleDateChange}
                    placeholder="Date"
                    name="date"
                    sx={{
                      width: 200,
                    }}
                  />
                  <LoadingButton
                    loading={isOrdersLoading}
                    onClick={onDateSubmit}
                    disabled={isDorButtonDisabled}
                    size="small"
                    variant="contained"
                  >
                    {t('load', { ns: 'common' })}
                  </LoadingButton>
                  <Button onClick={openLocationMapping} variant="outlined">{t('assignLocations', { ns: 'settings' })}</Button>
                </FormProvider>
              </Stack>
              <OrdersCountLoader />
            </Stack>
          )}
        />
        <DorAnalytics locationId={data?.dorIntegration?.locationsMapping?.[0]?.shopifyLocationId} />
      </Container>
    </PageHelmet>
  );
};

export default DorAnalyticsPage;
